import type { LifeOsState } from "../types";
import { budgetStatuses, monthKeyOf, monthLedger } from "./bento-derive";
import { convertToVnd } from "./currency-derive";

// ── D47: auto monthly-close prompt status (pure) ───────────────────────────────

/** YYYY-MM của tháng liền trước `today`. */
export function previousMonthKey(today: string): string {
  const [y, m] = today.slice(0, 7).split("-").map(Number);
  const d = new Date(y, m - 2, 1); // m is 1-based → tháng trước = m-2 (0-based)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export type MonthlyCloseStatus = {
  /** Tháng cần chốt (tháng vừa khép). */
  targetMonth: string;
  /** Có nên nhắc/chốt không: đã tới ngày chốt + tháng đó có hoạt động + chưa chốt. */
  pending: boolean;
};

/**
 * Trạng thái nhắc chốt tháng (D47). Quy tắc khớp mô tả người dùng:
 * "từ ngày chốt mỗi tháng, nhắc chốt THÁNG VỪA KHÉP".
 * - targetMonth = tháng liền trước hôm nay.
 * - pending khi: hôm nay đã qua `closeDay` của tháng hiện tại, tháng đích có ít
 *   nhất 1 giao dịch thật (khỏi nhắc tháng trống), và chưa có bản ghi chốt
 *   (weeklyReviews scope=month id=targetMonth).
 */
export function monthlyCloseStatus(
  state: Pick<LifeOsState, "transactions" | "weeklyReviews">,
  today: string,
  closeDay: number,
): MonthlyCloseStatus {
  const targetMonth = previousMonthKey(today);
  const dayReached = Number(today.slice(8, 10)) >= closeDay;
  const alreadyClosed = state.weeklyReviews.some((r) => r.scope === "month" && r.id === targetMonth);
  const hasActivity = state.transactions.some((t) => monthKeyOf(t.date) === targetMonth && !t.toAccountId);
  return { targetMonth, pending: dayReached && !alreadyClosed && hasActivity };
}

export type MonthlyCloseSummary = {
  monthKey: string;
  income: number;
  expense: number;
  net: number;
  savingsRate: number;
  budgetLimit: number;
  budgetSpent: number;
  overBudgetCount: number;
  topCategories: Array<{ category: string; amount: number }>;
  insights: string[];
  /** True khi có giao dịch ví ngoại tệ chưa có tỷ giá → bị tính = 0 trong tổng VND. */
  hasMissingRate: boolean;
};

export function monthlyCloseSummary(state: LifeOsState, monthKey: string, today: string): MonthlyCloseSummary {
  const ledger = monthLedger(state, monthKey, today);
  const income = ledger.filter((row) => row.type === "income").reduce((sum, row) => sum + row.amount, 0);
  const expense = ledger.filter((row) => row.type === "expense").reduce((sum, row) => sum + row.amount, 0);
  const net = income - expense;
  const savingsRate = income > 0 ? net / income : 0;
  const statuses = budgetStatuses(state, monthKey, today);
  const budgetLimit = statuses.reduce((sum, status) => sum + status.effectiveLimit, 0);
  const budgetSpent = statuses.reduce((sum, status) => sum + status.spent, 0);
  const overBudgetCount = statuses.filter((status) => status.over).length;

  const byCategory = new Map<string, number>();
  for (const row of ledger) {
    if (row.type === "expense") {
      byCategory.set(row.category, (byCategory.get(row.category) ?? 0) + row.amount);
    }
  }
  const topCategories = [...byCategory.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([category, amount]) => ({ category, amount }));

  // Đa tiền tệ: phát hiện giao dịch ví ngoại tệ thiếu tỷ giá (bị quy đổi = 0).
  // monthLedger nuốt im lặng các khoản này → cảnh báo để số chốt không bị hiểu nhầm.
  const hasMissingRate = state.transactions.some((t) => {
    if (monthKeyOf(t.date) !== monthKey || t.toAccountId) return false;
    const currency = state.accounts.find((a) => a.id === t.accountId)?.currency ?? "VND";
    return convertToVnd(t.amount, currency, state.currencyRates).missingRate;
  });

  const insights = [
    net >= 0 ? "Dòng tiền tháng dương." : "Dòng tiền tháng âm.",
    income > 0 ? `Tỷ lệ tiết kiệm ${(savingsRate * 100).toFixed(0)}%.` : "Chưa có thu nhập trong tháng.",
    overBudgetCount > 0 ? `${overBudgetCount} danh mục vượt ngân sách.` : "Không có danh mục vượt ngân sách.",
  ];

  return {
    monthKey,
    income,
    expense,
    net,
    savingsRate,
    budgetLimit,
    budgetSpent,
    overBudgetCount,
    topCategories,
    insights,
    hasMissingRate,
  };
}
